// Location data for all destination pages
// Each config contains all the content needed for the LocationPage component

export const cascaisConfig = {
  slug: 'cascais',
  badge: 'Your Home Base',
  imageKey: 'cascais',
  ctaTitle: 'Live in the Heart of It All',
  ctaDescription: 'Wake up on Rua Dom Francisco de Avilez, steps from beaches, restaurants, and the vibrant pulse of downtown Cascais. Your coastal dream starts here.',
  content: {
    title: 'Your Coastal Sanctuary',
    subtitle: 'Where pristine beaches meet historic charm. Experience the perfect blend of Portuguese tradition and cosmopolitan elegance.',
    paragraphs: [
      "Once a humble 12th-century fishing village, Cascais transformed forever when King Luís I chose it as the royal summer residence in the 1870s. Portuguese nobility followed, building Belle Époque palaces and grand hotels that turned this seaside town into Europe's most fashionable coastal playground—a status it maintains today.",
      "Today, Cascais balances its aristocratic heritage with laid-back beach culture. World-class surfing at Guincho, fresh seafood by the marina, boutique shopping along cobbled streets, and golden beaches just steps from 19th-century palaces—this is where Portuguese elegance meets the sea."
    ],
    stats: [
      { value: '10+', label: 'Beaches & Attractions', detail: 'Sandy shores • Marina • Historic sites' },
      { value: '850+', label: 'Years of History', detail: 'From Fishing Village to Royal Resort' },
      { value: '5', label: 'Minute Walk', detail: 'To beaches, restaurants & nightlife' }
    ]
  }
};

export const lisbonConfig = {
  slug: 'lisbon',
  badge: '30 min from Urban Oasis',
  imageKey: 'lisbon',
  ctaTitle: "Explore Portugal's Capital",
  ctaDescription: "Stay at Urban Oasis Cascais and experience Lisbon as the perfect day trip. Just 30 minutes away, a world of history, culture, and cuisine awaits.",
  content: {
    title: "Portugal's Vibrant Capital",
    subtitle: "From ancient Alfama to modern Belém, Lisbon captivates with its stunning vistas, rich history, and legendary hospitality.",
    paragraphs: [
      "Built on seven hills overlooking the Tagus River, Lisbon is one of Europe's oldest cities—predating London, Paris, and Rome by centuries. The great earthquake of 1755 destroyed much of the city, but from the ruins rose the elegant Baixa district, a masterpiece of Enlightenment urban planning.",
      "Today, Lisbon seamlessly blends its Moorish heritage, maritime history, and modern creativity. Ride the iconic Tram 28 through narrow Alfama streets, sample world-famous pastéis de nata, explore cutting-edge museums, and watch the sunset paint the city gold from countless miradouros."
    ],
    stats: [
      { value: '15+', label: 'Landmarks to Explore', detail: 'Castles • Museums • Viewpoints • Markets' },
      { value: '3000+', label: 'Years of History', detail: "One of Europe's Oldest Capitals" },
      { value: '30', label: 'Minute Journey', detail: 'By train or car from Urban Oasis' }
    ]
  }
};

export const sintraConfig = {
  slug: 'sintra',
  badge: 'UNESCO World Heritage Site',
  imageKey: 'sintra',
  ctaTitle: 'Your Perfect Base',
  ctaDescription: "Stay in modern comfort in Cascais. Explore Sintra's magic by day. Enjoy beaches and nightlife by evening.",
  content: {
    title: 'A Fairy-Tale Day Trip',
    subtitle: "Europe's most romantic town. Colorful palaces, medieval castles, and mystical gardens just 15 minutes from your door.",
    paragraphs: [
      "For eight centuries, Portuguese royalty escaped to Sintra's cool mountain mists, building elaborate palaces that Lord Byron immortalized as a \"glorious Eden\" in 1812. When King Ferdinand II transformed a ruined monastery into the vibrant Pena Palace in 1838, he sparked European Romanticism—blending Gothic spires, Moorish domes, and Renaissance gardens into architecture so fantastical it launched a continental movement.",
      "Today, UNESCO protects Sintra as Europe's first Cultural Landscape—a living fairy tale where colorful castles emerge from forests, spiral wells descend into mystical depths, and every palace tells a story of kings, poets, and dreamers who dared to build the impossible."
    ],
    stats: [
      { value: '8', label: 'Palaces & Monuments', detail: 'Pena • Regaleira • Moorish Castle & more' },
      { value: '1995', label: 'UNESCO Heritage Site', detail: "Europe's First Cultural Landscape" },
      { value: '$15', label: 'Easy Uber Ride', detail: 'Just 15 minutes away' }
    ]
  }
};

export const obidosConfig = {
  slug: 'obidos',
  badge: '1 hour from Urban Oasis',
  imageKey: 'obidos',
  ctaTitle: 'Discover Medieval Portugal',
  ctaDescription: "Stay at Urban Oasis Cascais and visit Óbidos as a magical day trip. Just one hour away, a fairytale town awaits within ancient walls.",
  content: {
    title: "Portugal's Medieval Walled Town",
    subtitle: 'Step through ancient gates into a perfectly preserved medieval world, where cobblestone streets wind past whitewashed houses and castle walls.',
    paragraphs: [
      "Perched on a hill and encircled by towering medieval walls, Óbidos is one of Portugal's most enchanting treasures. Conquered from the Moors in 1148 by Portugal's first king, Afonso I, the town became known as the \"Town of Queens\" after King Dinis gifted it to his bride, Queen Isabel, as a wedding present in 1282—a romantic tradition that continued for centuries.",
      "Today, Óbidos is a living museum and one of the Seven Wonders of Portugal. Walk the mile-long medieval walls for breathtaking views, sample the famous Ginjinha cherry liqueur served in chocolate cups, and lose yourself in the charm of Rua Direita, the main street lined with artisan shops, galleries, and traditional restaurants tucked into centuries-old buildings."
    ],
    stats: [
      { value: '7', label: 'Wonders of Portugal', detail: 'Voted national treasure in 2007' },
      { value: '800+', label: 'Years of History', detail: 'Castle built 12th-14th century' },
      { value: '1', label: 'Hour Drive', detail: 'Scenic route from Urban Oasis' }
    ]
  }
};

export const tomarConfig = {
  slug: 'tomar',
  badge: '2 hours from Urban Oasis',
  imageKey: 'tomar',
  ctaTitle: "Follow in the Templars' Footsteps",
  ctaDescription: "Stay at Urban Oasis Cascais and journey to Tomar for an unforgettable day trip. Two hours away, one of history's greatest mysteries awaits.",
  content: {
    title: 'City of the Knights Templar',
    subtitle: "Discover the legendary headquarters of the Knights Templar, where centuries of history unfold in one of Portugal's most remarkable UNESCO sites.",
    paragraphs: [
      "Founded in 1160 by Grand Master Gualdim Pais, Tomar was built as the Templars' stronghold during Portugal's Reconquest. The crowning jewel is the Convent of Christ, a vast 45-hectare complex featuring the iconic Charola—a 16-sided rotunda modeled after Jerusalem's Church of the Holy Sepulchre, where knights attended mass on horseback.",
      "After the Templars' dissolution in 1312, King Dinis cleverly reorganized them as the Order of Christ. Under Prince Henry the Navigator, Tomar became the nerve center of Portugal's Age of Discoveries. Seven centuries of construction added Gothic cloisters, the famous Manueline Window, and Renaissance masterpieces—all preserved as a UNESCO World Heritage site since 1983."
    ],
    stats: [
      { value: '1160', label: 'Founded by Templars', detail: 'Over 860 years of history' },
      { value: 'UNESCO', label: 'World Heritage Site', detail: 'Inscribed since 1983' },
      { value: '2', label: 'Hour Drive', detail: 'Scenic route from Urban Oasis' }
    ]
  }
};

export const evoraConfig = {
  slug: 'evora',
  badge: '2 hours from Urban Oasis',
  imageKey: 'evora',
  ctaTitle: 'Journey Through Time',
  ctaDescription: "Stay at Urban Oasis Cascais and explore Évora as an unforgettable day trip. Two hours through the Alentejo, a UNESCO treasure awaits.",
  content: {
    title: 'The Museum City of Alentejo',
    subtitle: "Walk through 2,000 years of history in Portugal's best-preserved historic city, where Roman temples stand beside Gothic cathedrals and Moorish influences.",
    paragraphs: [
      "Rising from the golden plains of the Alentejo, Évora is a living museum spanning over twenty centuries. From its Celtic origins through Roman domination, Visigoth rule, and Moorish occupation, the city reached its golden age in the 15th century when Portuguese kings chose it as their royal residence, filling it with convents, palaces, and the prestigious university founded in 1559.",
      "Today, Évora's whitewashed houses decorated with azulejo tiles, the iconic Roman Temple, the imposing Gothic cathedral, and the haunting Chapel of Bones create an unforgettable tapestry of Portuguese heritage. Named a UNESCO World Heritage site in 1986, it remains \"the brightest jewel of the golden age of Portugal.\""
    ],
    stats: [
      { value: '2000+', label: 'Years of History', detail: 'Celtic, Roman, Moorish, Portuguese' },
      { value: 'UNESCO', label: 'World Heritage Site', detail: 'Inscribed since 1986' },
      { value: '2', label: 'Hour Drive', detail: 'Through the Alentejo plains' }
    ]
  }
};

export const nazareConfig = {
  slug: 'nazare',
  badge: '1 hour from Urban Oasis',
  imageKey: 'nazare',
  ctaTitle: 'Witness the Power of the Atlantic',
  ctaDescription: "Stay at Urban Oasis Cascais and visit Nazaré as an exhilarating day trip. Just one hour away, where fishing traditions meet world-record waves.",
  content: {
    title: "Home of the World's Biggest Waves",
    subtitle: "Where a traditional fishing village meets world-record surfing, dramatic cliffs, and one of Portugal's most stunning coastlines.",
    paragraphs: [
      "Nazaré has captivated visitors for centuries with its golden crescent beach, colorful fishing boats, and women in traditional seven-skirt costumes. But in 2011, when Garrett McNamara surfed a 78-foot wave at Praia do Norte, this quiet village became the undisputed capital of big wave surfing. The Nazaré Canyon—a 16,000-foot underwater chasm—funnels Atlantic swells into monstrous walls of water, creating the largest surfable waves on Earth.",
      "Take the historic funicular to Sítio, the clifftop quarter with panoramic views, the 17th-century Sanctuary of Our Lady of Nazaré, and the lighthouse fortress where crowds gather to witness surfers challenge 80-foot waves. Whether you come for world-record surf or fresh-grilled sardines on the beach, Nazaré delivers an unforgettable Portuguese coastal experience."
    ],
    stats: [
      { value: '86ft', label: 'World Record Wave', detail: 'Sebastian Steudtner, 2020' },
      { value: '16K', label: 'Foot Deep Canyon', detail: 'Creates the giant waves' },
      { value: '1', label: 'Hour Drive', detail: 'Scenic coastal route' }
    ]
  }
};

export const penicheConfig = {
  slug: 'peniche',
  badge: '2 hours from Urban Oasis',
  imageKey: 'peniche',
  ctaTitle: 'Surf, Explore, Discover',
  ctaDescription: "Stay at Urban Oasis Cascais and experience Peniche as an unforgettable day trip. Two hours away, where Atlantic waves meet medieval history.",
  content: {
    title: "Portugal's Wave Capital",
    subtitle: 'Where world-class surfing meets medieval fortresses, traditional fishing villages, and a pristine island nature reserve just offshore.',
    paragraphs: [
      "Perched on a dramatic peninsula jutting into the Atlantic, Peniche has transformed from a medieval fishing port into Portugal's undisputed surfing mecca. The legendary Supertubos beach—dubbed the \"European Pipeline\"—hosts the annual World Surf League championship, drawing professional surfers from around the globe to ride its powerful, tubular waves.",
      "Beyond the surf, discover the imposing 16th-century Fortaleza de Peniche, now a museum of resistance and freedom, and catch a ferry to the protected Berlengas Islands—a nature reserve with crystal-clear waters, dramatic cliffs, and the fairy-tale São João Baptista fortress connected by a stone bridge. Traditional bobbin lace craftsmanship and fresh-off-the-boat seafood complete this authentic Portuguese coastal experience."
    ],
    stats: [
      { value: 'WSL', label: 'World Surf League Stop', detail: 'MEO Rip Curl Pro Portugal' },
      { value: '1609', label: 'Town Status Granted', detail: 'Over 400 years of history' },
      { value: '2', label: 'Hour Drive', detail: 'Scenic coastal route' }
    ]
  }
};

// Export all configs for easy access
export const locationConfigs = {
  cascais: cascaisConfig,
  lisbon: lisbonConfig,
  sintra: sintraConfig,
  obidos: obidosConfig,
  tomar: tomarConfig,
  evora: evoraConfig,
  nazare: nazareConfig,
  peniche: penicheConfig
};

export default locationConfigs;
